import { Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { PURCHASE_STATUSES } from '@sr/dto';
import { LegalEntityParamFilter } from '../../entities/legal-entities/legal-entity-param-filter';
import { ManagerUrlParamsFilter } from '../../entities/filters/manager-url-params-filter';
import { DateUrlParamsFilter } from '../../entities/filters/date-url-params-filter';
import { useOurCompany } from '../../hooks/portal-settings.hook';
import { SimpleParamFilter } from '../../shared/ui/param-filters/simple-param-filter';
import { FilterWrapper, ValueFormatterContext } from 'shared/ui/filter/filter-wrapper';
import { useManagersDropdownList } from 'features/user/user-picker/ui';
import { useLegalEntityLabel } from 'shared/hooks/use-entity-labels';

export const PurchasesFilter = () => {
  const ourCompanyId = useOurCompany();
  const { items: managers } = useManagersDropdownList();
  const [searchParams] = useSearchParams();
  const selectedSupplierLegalEntityId = searchParams.get('supplierLegalEntityId');
  const selectedClientLegalEntityId = searchParams.get('clientLegalEntityId');
  const supplierLegalEntityLabel = useLegalEntityLabel(selectedSupplierLegalEntityId);
  const clientLegalEntityLabel = useLegalEntityLabel(selectedClientLegalEntityId);

  const managerValueFormatter = ({ value }: ValueFormatterContext) => {
    const manager = managers.find((item) => String(item.id) === value);
    return manager ? `${manager.firstName} ${manager.secondName || ''}`.trim() : value;
  };

  const createOptionFormatter = (options: { id: number | string; label: string }[]) => {
    const map = new Map(options.map((item) => [String(item.id), item.label]));
    return ({ value }: ValueFormatterContext) => map.get(String(value)) ?? value;
  };

  const statusFormatter = createOptionFormatter(PURCHASE_STATUSES);

  const legalEntityValueFormatter = ({ value, key }: ValueFormatterContext) => {
    if (key === 'supplierLegalEntityId' && supplierLegalEntityLabel && selectedSupplierLegalEntityId === value) {
      return supplierLegalEntityLabel;
    }
    if (key === 'clientLegalEntityId' && clientLegalEntityLabel && selectedClientLegalEntityId === value) {
      return clientLegalEntityLabel;
    }
    return value;
  };

  return (
    <FilterWrapper title="Фильтры заявок">
      <FilterWrapper.Item
        label="Наше юрлицо"
        queryKeys={['supplierLegalEntityId', 'clientLegalEntityId']}
        valueFormatter={legalEntityValueFormatter}
        summaryFormatter={({ searchParams }) => {
          const supplierId = searchParams.get('supplierLegalEntityId');
          const clientId = searchParams.get('clientLegalEntityId');
          const values = [
            supplierId && `поставщик — ${supplierLegalEntityLabel || supplierId}`,
            clientId && `клиент — ${clientLegalEntityLabel || clientId}`,
          ].filter(Boolean);

          return values.length ? `Наше юрлицо: ${values.join('; ')}` : 'Наше юрлицо';
        }}
      >
        <Stack spacing={1.5}>
          <LegalEntityParamFilter queryParamName="supplierLegalEntityId" companyId={ourCompanyId} label="Расчёт с поставщиком" />
          <LegalEntityParamFilter queryParamName="clientLegalEntityId" companyId={ourCompanyId} label="Расчёт с клиентом" />
        </Stack>
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Статус" queryKeys="statusId" valueFormatter={statusFormatter}>
        <SimpleParamFilter queryParamName="statusId" items={PURCHASE_STATUSES} label="Статус" />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Менеджер" queryKeys="managerId" valueFormatter={managerValueFormatter}>
        <ManagerUrlParamsFilter queryParamName="managerId" />
      </FilterWrapper.Item>

      <FilterWrapper.Item
        label="Дата создания"
        queryKeys={['fromDate', 'toDate']}
        summaryFormatter={({ searchParams }) => {
          const from = searchParams.get('fromDate');
          const to = searchParams.get('toDate');
          if (!from && !to) return 'Дата создания';
          if (from && to) return `Создано: ${from} — ${to}`;
          return `Создано: ${from || to}`;
        }}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName={'fromDate'} label="Создан с" />
          <DateUrlParamsFilter queryParamName={'toDate'} label="Создан по" />
        </Stack>
      </FilterWrapper.Item>
    </FilterWrapper>
  );
};
